import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
  ArrowUpRight,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  X,
  Eye,
  Palette,
  Layers,
  Cpu,
  Megaphone,
  TrendingUp,
  Brain,
  Leaf,
  Workflow,
  Atom,
  Lightbulb,
  Target,
  MessageSquare,
  Users,
  Monitor,
  Compass,
  Bot,
  Network,
  CheckCircle2,
  BookOpen,
  Presentation,
  Trophy,
  Building2,
  Briefcase,
  Play,
  Pause,
  RotateCw,
  GraduationCap,
  PenTool,
  Landmark,
  Share2,
} from 'lucide-react';
import PageHeader from '../innovher/PageHeader';
import SEO from '../SEO';
import { getCompany } from '../../data/companyPages';

import { SITE_URL as SITE } from '../../lib/site';

const getDomainIcon = (text) => {
  const lower = text.toLowerCase();
  if (lower.includes('design and creative')) return Palette;
  if (lower.includes('architecture')) return Layers;
  if (lower.includes('technology') || lower.includes('digital')) return Cpu;
  if (lower.includes('communication') || lower.includes('media')) return Megaphone;
  if (lower.includes('business') || lower.includes('entrepreneurship')) return TrendingUp;
  if (lower.includes('education') || lower.includes('behaviour')) return Brain;
  if (lower.includes('culture') || lower.includes('craft') || lower.includes('heritage')) return Compass;
  if (lower.includes('sustainability') || lower.includes('social impact')) return Leaf;
  if (lower.includes('experience') || lower.includes('service')) return Workflow;
  if (lower.includes('science') || lower.includes('well-being')) return Atom;

  if (lower.includes('creative and critical') || lower.includes('critical thinking')) return Lightbulb;
  if (lower.includes('problem-solving')) return Target;
  if (lower.includes('presentation') || lower.includes('communication and')) return MessageSquare;
  if (lower.includes('collaboration') || lower.includes('leadership')) return Users;
  if (lower.includes('literacy')) return Monitor;
  if (lower.includes('adaptability')) return Compass;
  if (lower.includes('ai-augmented') || lower.includes('ai')) return Bot;
  if (lower.includes('systems') || lower.includes('interdisciplinary')) return Network;

  return CheckCircle2;
};

function VentureGallery({ company }) {
  const images = company.gallery || [];
  const [activeIdx, setActiveIdx] = useState(null);

  const total = images.length;
  if (!total) return null;

  const [hero, second, ...rest] = images;
  const totalRest = rest.length;

  const handlePrev = useCallback((e) => {
    e?.stopPropagation();
    setActiveIdx((prev) => (prev === null ? null : (prev - 1 + total) % total));
  }, [total]);

  const handleNext = useCallback((e) => {
    e?.stopPropagation();
    setActiveIdx((prev) => (prev === null ? null : (prev + 1) % total));
  }, [total]);

  const handleClose = useCallback((e) => {
    e?.stopPropagation();
    setActiveIdx(null);
  }, []);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (activeIdx === null) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [activeIdx, handleClose, handleNext, handlePrev]);

  // Dynamic span calculation to ensure trailing rows never have empty gaps
  const getRestSpanClass = (index) => {
    const remainder = totalRest % 4;
    const isTrailingRow = remainder !== 0 && index >= totalRest - remainder;

    if (isTrailingRow) {
      if (remainder === 1) {
        return 'col-span-12 aspect-[21/9] sm:aspect-[16/7]';
      }
      if (remainder === 2) {
        return 'col-span-12 sm:col-span-6 lg:col-span-6 aspect-[16/9]';
      }
      if (remainder === 3) {
        return 'col-span-12 sm:col-span-4 lg:col-span-4 aspect-[16/10]';
      }
    }
    return 'col-span-6 sm:col-span-4 lg:col-span-3 aspect-[4/3]';
  };

  return (
    <div className="mt-24 relative overflow-hidden">
      {/* Ambient background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] max-w-full h-[400px] bg-[#d42b92]/8 blur-[160px] pointer-events-none -z-10" />

      {/* Gallery Header */}
      <div className="grid lg:grid-cols-12 gap-6 items-end mb-8 border-b border-white/5 pb-6">
        <div className="lg:col-span-8">
          {/* {company.galleryEyebrow ? (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[#ff8ec5] text-xs font-semibold tracking-[0.25em] uppercase mb-3 backdrop-blur-md">
              <span className="w-1.5 h-1.5 rounded-full bg-[#d42b92] inline-block animate-pulse" />
              <span>{company.galleryEyebrow}</span>
            </div>
          ) : null} */}
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight">
            {company.galleryTitle || `Inside ${company.name}`}
          </h2>
        </div>

        <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col items-start lg:items-end justify-between gap-3">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/10 text-white/70 text-xs font-medium">
            <Eye className="w-3.5 h-3.5 text-[#ff8ec5]" />
            <span>{total} Curated Moments</span>
            <span className="text-white/30">·</span>
            <span className="text-[11px] text-white/50">Click to expand</span>
          </div>
          <p className="text-white/50 text-xs sm:text-sm leading-relaxed lg:text-right">
            Moments from the {company.name} ecosystem, people, and the work of building.
          </p>
        </div>
      </div>

      {/* Gallery Grid */}
      <div className="grid grid-cols-12 gap-3 sm:gap-4 lg:gap-5">
        {/* Hero Shot (Index 0) */}
        <div
          onClick={() => setActiveIdx(0)}
          className="col-span-12 lg:col-span-7 rounded-3xl overflow-hidden border border-white/10 group relative aspect-[16/10] bg-white/[0.02] cursor-pointer shadow-lg hover:border-[#ff8ec5]/60 hover:shadow-[0_15px_40px_-10px_rgba(212,43,146,0.35)] transition-all duration-300"
        >
          <img
            src={hero}
            alt={`${company.name}, featured`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
            loading="lazy"
          />
          {/* Badge */}
          {/* <div className="absolute top-4 left-4 z-20 px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/15 text-[11px] font-mono font-medium text-white/90 flex items-center gap-1.5 shadow-md">
            <span className="w-1.5 h-1.5 rounded-full bg-[#d42b92] shadow-sm shadow-[#d42b92]" />
            <span>Featured · 01</span>
          </div> */}

          {/* Centered Expand Icon on hover */}
          <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
            <div className="w-12 h-12 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:scale-100 scale-75 transition-all duration-300 shadow-2xl shadow-black/60">
              <Maximize2 className="w-5 h-5" />
            </div>
          </div>

          {/* Bottom vignette bar */}
          <div className="absolute inset-x-0 bottom-0 z-20 p-5 bg-gradient-to-t from-black/85 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-between">
            <span className="text-xs font-medium text-white/90">{company.name} · Hero Showcase</span>
            <span className="text-[11px] text-[#ff8ec5] font-semibold flex items-center gap-1">
              Expand View <ArrowUpRight className="w-3.5 h-3.5" />
            </span>
          </div>
        </div>

        {/* Second Shot (Index 1) */}
        {second ? (
          <div
            onClick={() => setActiveIdx(1)}
            className="col-span-12 sm:col-span-6 lg:col-span-5 rounded-3xl overflow-hidden border border-white/10 group relative aspect-[4/3] lg:aspect-auto lg:h-full min-h-[240px] bg-white/[0.02] cursor-pointer shadow-lg hover:border-[#ff8ec5]/60 hover:shadow-[0_15px_40px_-10px_rgba(212,43,146,0.35)] transition-all duration-300"
          >
            <img
              src={second}
              alt=""
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
              loading="lazy"
            />
            {/* Badge */}
            {/* <div className="absolute top-4 left-4 z-20 px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/15 text-[11px] font-mono font-medium text-white/90 flex items-center gap-1.5 shadow-md">
              <span>02</span>
            </div> */}

            {/* Centered Expand Icon on hover */}
            <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
              <div className="w-12 h-12 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:scale-100 scale-75 transition-all duration-300 shadow-2xl shadow-black/60">
                <Maximize2 className="w-5 h-5" />
              </div>
            </div>

            {/* Bottom vignette bar */}
            <div className="absolute inset-x-0 bottom-0 z-20 p-5 bg-gradient-to-t from-black/85 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-between">
              <span className="text-xs font-medium text-white/90">{company.name} · Studio Perspective</span>
              <span className="text-[11px] text-[#ff8ec5] font-semibold flex items-center gap-1">
                Expand View <ArrowUpRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </div>
        ) : null}

        {/* Remaining Photos (Indices 2..N) */}
        {rest.map((src, i) => {
          const globalIdx = i + 2;
          const displayNum = globalIdx < 9 ? `0${globalIdx + 1}` : `${globalIdx + 1}`;

          return (
            <div
              key={src}
              onClick={() => setActiveIdx(globalIdx)}
              className={`${getRestSpanClass(
                i
              )} rounded-2xl sm:rounded-3xl overflow-hidden border border-white/10 group relative bg-white/[0.02] cursor-pointer shadow-md hover:border-[#ff8ec5]/60 hover:shadow-[0_12px_35px_-8px_rgba(212,43,146,0.3)] transition-all duration-300`}
            >
              <img
                src={src}
                alt=""
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                loading="lazy"
              />

              {/* Number Badge */}
              {/* <div className="absolute top-3 left-3 z-20 px-2.5 py-0.5 rounded-full bg-black/60 backdrop-blur-md border border-white/15 text-[10px] font-mono font-medium text-white/80 flex items-center gap-1 shadow-sm">
                <span>{displayNum}</span>
              </div> */}

              {/* Hover Centered Expand Icon */}
              <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
                <div className="w-10 h-10 rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:scale-100 scale-75 transition-all duration-300 shadow-xl shadow-black/50">
                  <Maximize2 className="w-4 h-4" />
                </div>
              </div>

              {/* Bottom vignette on hover */}
              <div className="absolute inset-x-0 bottom-0 z-20 p-3.5 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-between">
                <span className="text-[11px] font-medium text-white/80 truncate">
                  {company.name} #{displayNum}
                </span>
                <span className="text-[10px] text-[#ff8ec5] font-semibold">Expand</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ========================================================
          FULLSCREEN LIGHTBOX MODAL
         ======================================================== */}
      {activeIdx !== null && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-2xl flex flex-col justify-between p-4 sm:p-6 transition-opacity duration-200 animate-in fade-in"
          onClick={handleClose}
        >
          {/* Top Controls Bar */}
          <div
            className="flex items-center justify-between z-10 max-w-7xl mx-auto w-full pt-1"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 rounded-full bg-[#d42b92] shadow-sm shadow-[#d42b92]" />
              <div className="text-white font-semibold text-sm sm:text-base">
                {company.name} <span className="text-white/40">·</span>{' '}
                <span className="text-[#ff8ec5]">Inside Gallery</span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="px-3.5 py-1 rounded-full bg-white/10 border border-white/15 text-xs text-white/80 font-mono font-medium">
                {activeIdx + 1} / {total}
              </div>

              <button
                type="button"
                onClick={handleClose}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white flex items-center justify-center transition-all cursor-pointer group"
                title="Close (Esc)"
              >
                <X className="w-5 h-5 group-hover:rotate-90 transition-transform duration-200" />
              </button>
            </div>
          </div>

          {/* Central Image View with Left/Right Arrows */}
          <div
            className="relative flex-1 flex items-center justify-center my-3 max-w-6xl mx-auto w-full select-none"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Prev Button */}
            <button
              type="button"
              onClick={handlePrev}
              className="absolute left-2 sm:left-4 z-20 w-11 h-11 sm:w-13 sm:h-13 rounded-full bg-black/50 hover:bg-[#d42b92] border border-white/20 hover:border-transparent text-white flex items-center justify-center transition-all duration-200 cursor-pointer shadow-2xl backdrop-blur-md group"
              title="Previous Photo (←)"
            >
              <ChevronLeft className="w-6 h-6 group-hover:-translate-x-0.5 transition-transform" />
            </button>

            {/* Displayed Image */}
            <div className="relative max-h-[70vh] sm:max-h-[75vh] w-auto max-w-full flex items-center justify-center overflow-hidden rounded-2xl border border-white/10 shadow-2xl shadow-black/80">
              <img
                src={images[activeIdx]}
                alt={`${company.name} moment ${activeIdx + 1}`}
                className="max-h-[70vh] sm:max-h-[75vh] w-auto max-w-full object-contain"
              />
            </div>

            {/* Next Button */}
            <button
              type="button"
              onClick={handleNext}
              className="absolute right-2 sm:right-4 z-20 w-11 h-11 sm:w-13 sm:h-13 rounded-full bg-black/50 hover:bg-[#d42b92] border border-white/20 hover:border-transparent text-white flex items-center justify-center transition-all duration-200 cursor-pointer shadow-2xl backdrop-blur-md group"
              title="Next Photo (→)"
            >
              <ChevronRight className="w-6 h-6 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>

          {/* Bottom Thumbnails Strip & Keyboard Guide */}
          <div
            className="z-10 max-w-5xl mx-auto w-full flex flex-col items-center gap-3 pb-1"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Thumbnail Strip */}
            <div className="flex items-center gap-2 overflow-x-auto max-w-full py-1 px-2 scrollbar-none">
              {images.map((imgSrc, idx) => {
                const isSelected = idx === activeIdx;
                return (
                  <button
                    key={imgSrc}
                    type="button"
                    onClick={() => setActiveIdx(idx)}
                    className={`relative w-14 h-10 sm:w-16 sm:h-11 rounded-lg overflow-hidden border shrink-0 transition-all duration-200 cursor-pointer ${isSelected
                      ? 'border-[#ff8ec5] ring-2 ring-[#d42b92] scale-105 shadow-md shadow-[#d42b92]/30'
                      : 'border-white/20 opacity-50 hover:opacity-100'
                      }`}
                  >
                    <img
                      src={imgSrc}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </button>
                );
              })}
            </div>

            {/* Keyboard Guide */}
            <div className="text-[11px] text-white/40 hidden sm:flex items-center gap-2">
              <span>Use <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white/70">←</kbd> <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white/70">→</kbd> to navigate</span>
              <span>·</span>
              <span><kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white/70">Esc</kbd> to close</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CompanyProseSection({ prose }) {
  if (!prose || !prose.length) return null;

  return (
    <div className="mt-24 relative overflow-hidden">
      {/* Ambient background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] max-w-full h-[350px] bg-[#d42b92]/6 blur-[160px] pointer-events-none -z-10" />

      <div className={`grid ${prose.length === 1 ? 'max-w-4xl mx-auto' : 'lg:grid-cols-2'} gap-6 lg:gap-8 items-stretch`}>
        {prose.map((section, idx) => {
          const displayIdx = idx < 9 ? `0${idx + 1}` : `${idx + 1}`;
          const leadParagraph = section.paragraphs[0];
          const remainingParagraphs = section.paragraphs.slice(1);

          return (
            <div
              key={section.title}
              className="rounded-[2rem] border border-white/10 bg-gradient-to-br from-white/[0.04] via-white/[0.015] to-transparent p-7 sm:p-9 relative overflow-hidden backdrop-blur-md shadow-xl hover:border-[#ff8ec5]/40 transition-all duration-300 flex flex-col justify-between group"
            >
              {/* Top ambient highlight line */}
              <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#d42b92] to-transparent opacity-80" />

              <div>
                {/* Eyebrow badge with pulse */}
                {/* <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[#ff8ec5] text-[11px] font-mono tracking-[0.2em] uppercase mb-4 backdrop-blur-md">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d42b92] animate-pulse" />
                  <span>{displayIdx} · PERSPECTIVE</span>
                </div> */}

                {/* Title */}
                <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight leading-snug mb-5">
                  {section.title}
                </h2>

                {/* First paragraph as highlighted editorial hook */}
                {leadParagraph && (
                  <div className="text-base sm:text-lg text-white/95 font-medium leading-relaxed pl-4 border-l-2 border-[#d42b92] bg-white/[0.02] py-2 rounded-r-xl">
                    {leadParagraph}
                  </div>
                )}

                {/* Remaining paragraphs as supporting copy */}
                {remainingParagraphs.map((p) => (
                  <p
                    key={p}
                    className="text-sm sm:text-base text-white/70 leading-relaxed pl-4 mt-4"
                  >
                    {p}
                  </p>
                ))}
              </div>

              {/* Bottom decorative bar */}
              <div className="pt-6 mt-6 flex items-center justify-between border-t border-white/5 text-[11px] text-white/40">
                <span className="font-mono">InnovHer Ecosystem</span>
                <span className="text-[#ff8ec5] font-semibold">Applied Standard</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CompanyListSection({ list, index }) {
  if (!list || !list.items?.length) return null;

  const count = list.items.length;
  // Compact 4-column layout for skills or <= 8 items, 2-column for 10+ items
  const isCompactGrid = count <= 8;

  return (
    <div className="mt-24 relative overflow-hidden">
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/3 -translate-x-1/2 -translate-y-1/2 w-[550px] max-w-full h-[300px] bg-[#d42b92]/6 blur-[150px] pointer-events-none -z-10" />

      {/* Header */}
      <div className="grid lg:grid-cols-12 gap-6 items-end mb-8 border-b border-white/5 pb-6">
        <div className="lg:col-span-8">
          {/* <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[#ff8ec5] text-xs font-semibold tracking-[0.25em] uppercase mb-3 backdrop-blur-md">
            <span className="w-1.5 h-1.5 rounded-full bg-[#d42b92] animate-pulse" />
            <span>{count} FOCUS AREAS</span>
          </div> */}

          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight">
            {list.title}
          </h2>

          {list.intro && (
            <p className="mt-3 text-white/75 text-base sm:text-lg leading-relaxed max-w-3xl">
              {list.intro}
            </p>
          )}
        </div>

        <div className="lg:col-span-4 lg:justify-self-end">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/[0.03] border border-white/10 text-white/70 text-xs font-mono">
            <span>{count} Connected Components</span>
          </div>
        </div>
      </div>

      {/* Grid of Cards */}
      <div
        className={
          isCompactGrid
            ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-4'
            : 'grid grid-cols-1 md:grid-cols-2 gap-3.5 sm:gap-4'
        }
      >
        {list.items.map((item, itemIdx) => {
          const ItemIcon = getDomainIcon(item);
          const displayIdx = itemIdx < 9 ? `0${itemIdx + 1}` : `${itemIdx + 1}`;

          return (
            <div
              key={item}
              className={`rounded-2xl border border-white/10 bg-white/[0.025] hover:bg-white/[0.05] hover:border-[#ff8ec5]/50 p-4 sm:p-5 transition-all duration-300 hover:-translate-y-0.5 group shadow-sm hover:shadow-[0_10px_25px_-5px_rgba(212,43,146,0.2)] ${isCompactGrid
                ? 'flex flex-col justify-between min-h-[140px]'
                : 'flex items-center gap-4'
                }`}
            >
              {isCompactGrid ? (
                <>
                  <div className="flex items-center justify-between w-full mb-3">
                    <div className="w-10 h-10 rounded-xl bg-white/[0.04] text-[#ff8ec5] group-hover:bg-[#d42b92] group-hover:text-white border border-white/10 group-hover:border-transparent flex items-center justify-center shrink-0 transition-colors duration-300 shadow-inner">
                      <ItemIcon className="w-5 h-5" />
                    </div>
                    <span className="text-[11px] font-mono text-white/35 group-hover:text-[#ff8ec5] transition-colors">
                      {displayIdx}
                    </span>
                  </div>
                  <h3 className="text-sm sm:text-[15px] font-semibold text-white group-hover:text-white leading-snug">
                    {item}
                  </h3>
                </>
              ) : (
                <>
                  <div className="w-11 h-11 rounded-xl bg-white/[0.04] text-[#ff8ec5] group-hover:bg-[#d42b92] group-hover:text-white border border-white/10 group-hover:border-transparent flex items-center justify-center shrink-0 transition-colors duration-300 shadow-inner">
                    <ItemIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
                    <h3 className="text-sm sm:text-[15px] font-semibold text-white group-hover:text-white leading-snug">
                      {item}
                    </h3>
                    <span className="text-[11px] font-mono text-white/35 group-hover:text-[#ff8ec5] transition-colors shrink-0">
                      {displayIdx}
                    </span>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const ECOSYSTEM_ICONS = [
  Brain,
  BookOpen,
  Users,
  Presentation,
  Trophy,
  Building2,
  Briefcase,
];

const getEcosystemCenterMeta = (company, block) => {
  if (company?.slug === 'beyond-ability-x') {
    return {
      brand: 'BAX',
      subtitle: 'Sports Ecosystem',
      accent: '#c084fc',
      accent2: '#ff3b6d',
      gradient: 'from-[#c084fc] via-[#a855f7] to-[#ff3b6d]',
      glow: 'rgba(168,85,247,0.45)',
    };
  }
  if (company?.slug === 'encode') {
    return {
      brand: 'enCODE',
      subtitle: 'Learning Ecosystem',
      accent: '#ff8ec5',
      accent2: '#ffd043',
      gradient: 'from-[#ff8ec5] via-[#ff5ea7] to-white',
      glow: 'rgba(212,43,146,0.45)',
    };
  }
  if (company?.slug === 'bharat-ventures') {
    return {
      brand: 'Bharat Ventures',
      subtitle: 'Startup Ecosystem',
      accent: '#fb923c',
      accent2: '#06b6d4',
      gradient: 'from-[#fb923c] via-[#f97316] to-[#38bdf8]',
      glow: 'rgba(249,115,22,0.45)',
    };
  }
  if (company?.slug === 'ctpl') {
    return {
      brand: 'CTPL',
      subtitle: 'Education Ecosystem',
      accent: '#60a5fa',
      accent2: '#34d399',
      gradient: 'from-[#60a5fa] via-[#3b82f6] to-[#34d399]',
      glow: 'rgba(59,130,246,0.45)',
    };
  }
  if (company?.slug === 'i2i') {
    return {
      brand: 'i2i',
      subtitle: 'Venture Engine',
      accent: '#f43f5e',
      accent2: '#a855f7',
      gradient: 'from-[#f43f5e] via-[#e11d48] to-[#c084fc]',
      glow: 'rgba(225,29,72,0.45)',
    };
  }
  if (company?.slug === 'innovz') {
    return {
      brand: 'InnovZ',
      subtitle: 'Next-Gen Ecosystem',
      accent: '#22d3ee',
      accent2: '#fbbf24',
      gradient: 'from-[#22d3ee] via-[#06b6d4] to-[#f59e0b]',
      glow: 'rgba(6,182,212,0.45)',
    };
  }

  const cleanTitle = (block?.title || '')
    .replace(/^The\s+/i, '')
    .replace(/\s+ecosystem$/i, '')
    .trim();

  return {
    brand: cleanTitle || company?.name || 'InnovHer',
    subtitle: `${company?.category || 'Venture'} Ecosystem`,
    accent: '#ff8ec5',
    accent2: '#d42b92',
    gradient: 'from-[#ff8ec5] via-[#ff5ea7] to-white',
    glow: 'rgba(212,43,146,0.45)',
  };
};

const renderCenterBrand = (meta, company) => {
  if (company?.slug === 'encode') {
    return (
      <h3 className="relative z-10 text-3xl font-black tracking-tight text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]">
        en<span className="bg-gradient-to-r from-[#ff8ec5] via-[#ff5ea7] to-white bg-clip-text text-transparent">CODE</span>
      </h3>
    );
  }

  return (
    <h3 className="relative z-10 text-3xl sm:text-4xl font-black tracking-tight text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]">
      <span className={`bg-gradient-to-r ${meta.gradient} bg-clip-text text-transparent`}>
        {meta.brand}
      </span>
    </h3>
  );
};

function CtplAdmissionsPipelineBlock({ block, company }) {
  const items = block.items || [];
  const [activeStep, setActiveStep] = useState(null);

  const stageMeta = [
    {
      step: '01',
      phase: 'Inbound Outreach',
      tag: 'Top-of-Funnel',
      accent: '#38bdf8',
      glow: 'rgba(56,189,248,0.28)',
      gradient: 'from-[#38bdf8] to-[#0284c7]',
    },
    {
      step: '02',
      phase: 'Centralized CRM',
      tag: 'Lead Capture',
      accent: '#60a5fa',
      glow: 'rgba(96,165,250,0.28)',
      gradient: 'from-[#60a5fa] to-[#2563eb]',
    },
    {
      step: '03',
      phase: 'Automated Nurture',
      tag: 'Personalised Counselling',
      accent: '#818cf8',
      glow: 'rgba(129,140,248,0.28)',
      gradient: 'from-[#818cf8] to-[#4f46e5]',
    },
    {
      step: '04',
      phase: 'Digital Onboarding',
      tag: 'Conversion & Enrolment',
      accent: '#34d399',
      glow: 'rgba(52,211,153,0.28)',
      gradient: 'from-[#34d399] to-[#059669]',
    },
    {
      step: '05',
      phase: 'Real-Time Analytics',
      tag: 'Institutional Intelligence',
      accent: '#2dd4bf',
      glow: 'rgba(45,212,191,0.28)',
      gradient: 'from-[#2dd4bf] to-[#0d9488]',
    },
    {
      step: '06',
      phase: 'Impact & Reputation',
      tag: 'Brand Equity',
      accent: '#a78bfa',
      glow: 'rgba(167,139,250,0.28)',
      gradient: 'from-[#a78bfa] to-[#7c3aed]',
    },
  ];

  const stageIcons = [
    // 01: Outreach / Radar
    <svg key="01" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth={1.8} />
      <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth={1.8} />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      <path strokeLinecap="round" d="M12 3v2m0 14v2M3 12h2m14 0h2" />
    </svg>,
    // 02: Funnel / Lead CRM
    <svg key="02" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V19l-4 2v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
    </svg>,
    // 03: Interactive dialogue / nurture
    <svg key="03" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>,
    // 04: Verified enrolment / application
    <svg key="04" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h3" />
    </svg>,
    // 05: Analytics telemetry / charts
    <svg key="05" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>,
    // 06: Shield of reputation / credibility
    <svg key="06" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>,
  ];

  return (
    <div className="mt-28 relative">
      {/* Background ambient lighting */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[850px] max-w-full h-[380px] bg-gradient-to-r from-[#38bdf8]/10 via-[#60a5fa]/8 to-[#34d399]/10 blur-[150px] pointer-events-none -z-10" />

      {/* Section Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 border-b border-white/10 pb-8">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#38bdf8]/10 border border-[#38bdf8]/30 text-[#38bdf8] text-xs font-semibold tracking-[0.25em] uppercase mb-3 backdrop-blur-md">
            <span className="w-1.5 h-1.5 rounded-full bg-[#38bdf8] animate-pulse" />
            <span>ADMISSIONS PIPELINE · 6-STAGE SEQUENTIAL ARCHITECTURE</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight">
            {block.title}
          </h2>
          <p className="mt-2 text-white/60 text-sm sm:text-base leading-relaxed max-w-2xl">
            A continuous, connected pipeline turning student interest into verified enrolments through structured CRM workflows and real-time intelligence.
          </p>
        </div>

        {/* Pipeline Phase Indicator */}
        <div className="hidden sm:inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/[0.03] border border-white/10 text-white/70 text-xs font-mono shrink-0">
          <span className="w-2 h-2 rounded-full bg-[#38bdf8] animate-pulse" />
          <span>Stage 01 → 06 Continuous Flow</span>
        </div>
      </div>

      {/* Desktop / Tablet Process Pipeline Flow */}
      <div className="hidden md:block space-y-8">
        {/* Luminous Interactive Process Track Bar */}
        <div className="relative rounded-2xl p-4 bg-gradient-to-r from-[#0c1426]/90 via-[#0a0f1d]/90 to-[#080d19]/90 border border-white/10 backdrop-blur-xl">
          <div className="flex items-center justify-between relative">
            {/* Connecting Track Line */}
            <div className="absolute left-6 right-6 top-1/2 -translate-y-1/2 h-[2px] bg-gradient-to-r from-[#38bdf8]/40 via-[#818cf8]/40 to-[#a78bfa]/40 -z-0 pointer-events-none" />

            {stageMeta.map((meta, idx) => {
              const isHovered = activeStep === idx;
              return (
                <div
                  key={meta.step}
                  onMouseEnter={() => setActiveStep(idx)}
                  onMouseLeave={() => setActiveStep(null)}
                  className="relative z-10 flex flex-col items-center group cursor-pointer transition-all duration-200"
                >
                  {/* Glowing Node Circle */}
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center font-mono text-xs font-bold transition-all duration-300 border ${isHovered
                      ? 'scale-110 text-white shadow-lg'
                      : 'bg-[#0f172a] text-white/80 border-white/20'
                      }`}
                    style={{
                      backgroundColor: isHovered ? meta.accent : '#0b1120',
                      borderColor: isHovered ? meta.accent : 'rgba(255,255,255,0.2)',
                      boxShadow: isHovered ? `0 0 20px ${meta.accent}` : 'none',
                    }}
                  >
                    {meta.step}
                  </div>
                  {/* Step Title Label */}
                  <span
                    className={`mt-2 text-[11px] font-medium transition-colors ${isHovered ? 'text-white font-semibold' : 'text-white/50'
                      }`}
                  >
                    {items[idx]?.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* 6 Connected Pipeline Cards in 2 Sequential Rows of 3 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 relative">
          {items.map((item, idx) => {
            const meta = stageMeta[idx] || stageMeta[0];
            const icon = stageIcons[idx] || stageIcons[0];
            const isHovered = activeStep === idx;

            return (
              <article
                key={item.title}
                onMouseEnter={() => setActiveStep(idx)}
                onMouseLeave={() => setActiveStep(null)}
                className={`relative rounded-3xl p-7 bg-gradient-to-b from-[#11192e]/85 via-[#0b101e]/85 to-[#070b14]/90 border transition-all duration-300 backdrop-blur-xl flex flex-col justify-between overflow-hidden group ${isHovered
                  ? '-translate-y-1.5 shadow-2xl'
                  : 'hover:-translate-y-1'
                  }`}
                style={{
                  borderColor: isHovered ? `${meta.accent}90` : 'rgba(255,255,255,0.1)',
                  boxShadow: isHovered ? `0 16px 45px ${meta.glow}` : '0 8px 30px rgba(0,0,0,0.3)',
                }}
              >
                {/* Top Glowing Flow Line */}
                <div
                  className="absolute top-0 inset-x-0 h-[2.5px] transition-opacity duration-300"
                  style={{
                    background: `linear-gradient(to right, transparent, ${meta.accent}, transparent)`,
                    opacity: isHovered ? 1 : 0.4,
                  }}
                />

                {/* Subtle Hover Gradient Flare */}
                <div
                  className="absolute -top-10 -right-10 w-28 h-28 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ background: meta.accent }}
                />

                <div>
                  {/* Card Header: Icon & Stage Sequence Pill */}
                  <div className="flex items-center justify-between gap-4 mb-6">
                    <div
                      className="w-11 h-11 rounded-2xl flex items-center justify-center border transition-transform duration-300 group-hover:scale-110 shrink-0"
                      style={{
                        backgroundColor: `${meta.accent}15`,
                        borderColor: `${meta.accent}40`,
                        color: meta.accent,
                        boxShadow: `0 0 20px ${meta.accent}20`,
                      }}
                    >
                      {icon}
                    </div>

                    <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 group-hover:border-white/20 transition-colors">
                      <span
                        className="font-mono text-xs font-bold"
                        style={{ color: meta.accent }}
                      >
                        STAGE {meta.step}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider text-white/50 font-medium">
                        {meta.tag}
                      </span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-white tracking-tight group-hover:text-white transition-colors">
                    {item.title}
                  </h3>

                  {/* Exact Text Content */}
                  <p className="mt-3 text-sm text-white/65 leading-relaxed group-hover:text-white/80 transition-colors">
                    {item.text}
                  </p>
                </div>

                {/* Card Footer: Sequence Progression Track */}
                <div className="mt-7 pt-5 border-t border-white/[0.06] flex items-center justify-between text-[11px] text-white/45">
                  <span className="font-mono">Phase: {meta.phase}</span>
                  <div className="flex items-center gap-1.5">
                    {[...Array(6)].map((_, i) => (
                      <span
                        key={i}
                        className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? 'w-5' : 'w-1.5 bg-white/15'
                          }`}
                        style={{
                          backgroundColor: i === idx ? meta.accent : undefined,
                        }}
                      />
                    ))}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      {/* Mobile: Purpose-Built Vertical Sequential Pipeline (screens < 768px) */}
      <div className="block md:hidden relative pl-6">
        {/* Continuous Vertical Glowing Spine */}
        <div className="absolute left-[15px] top-4 bottom-8 w-[2px] bg-gradient-to-b from-[#38bdf8] via-[#818cf8] to-[#a78bfa] pointer-events-none" />

        <div className="space-y-6">
          {items.map((item, idx) => {
            const meta = stageMeta[idx] || stageMeta[0];
            const icon = stageIcons[idx] || stageIcons[0];

            return (
              <div key={item.title} className="relative">
                {/* Vertical Spine Node Circle */}
                <div
                  className="absolute -left-[27px] top-6 w-7 h-7 rounded-full flex items-center justify-center font-mono text-[10px] font-bold text-white z-10 border-2"
                  style={{
                    backgroundColor: '#0a0f1d',
                    borderColor: meta.accent,
                    boxShadow: `0 0 14px ${meta.accent}`,
                  }}
                >
                  {meta.step}
                </div>

                {/* Mobile Stage Card */}
                <article
                  className="rounded-2xl p-5 bg-gradient-to-b from-[#11192e]/90 via-[#0b101e]/90 to-[#070b14]/95 border border-white/10 backdrop-blur-xl relative overflow-hidden"
                  style={{
                    boxShadow: `0 6px 25px rgba(0,0,0,0.3)`,
                  }}
                >
                  <div
                    className="absolute top-0 inset-x-0 h-[2px]"
                    style={{
                      background: `linear-gradient(to right, transparent, ${meta.accent}, transparent)`,
                    }}
                  />

                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center border shrink-0"
                      style={{
                        backgroundColor: `${meta.accent}15`,
                        borderColor: `${meta.accent}40`,
                        color: meta.accent,
                      }}
                    >
                      {icon}
                    </div>

                    <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/10 text-[10px] font-mono">
                      <span style={{ color: meta.accent }} className="font-bold">
                        {meta.step}
                      </span>
                      <span className="text-white/50">{meta.tag}</span>
                    </div>
                  </div>

                  <h3 className="text-lg font-bold text-white tracking-tight">
                    {item.title}
                  </h3>

                  <p className="mt-2 text-xs sm:text-sm text-white/70 leading-relaxed">
                    {item.text}
                  </p>

                  <div className="mt-4 pt-3 border-t border-white/[0.06] flex items-center justify-between text-[10px] text-white/40">
                    <span className="font-mono">Phase {idx + 1} of 6</span>
                    <span style={{ color: meta.accent }} className="font-medium">
                      {meta.phase}
                    </span>
                  </div>
                </article>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function BharatVenturesPillarBlock({ block }) {
  const items = block.items || [];
  const [activeIdx, setActiveIdx] = useState(0);

  const PILLAR_ICONS = [
    // 01 Venture Strategy
    <svg key="01" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>,
    // 02 Market Readiness
    <svg key="02" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <circle cx="11" cy="11" r="8" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35" />
    </svg>,
    // 03 Business Building
    <svg key="03" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3M9 7h1m-1 4h1m4-4h1m-1 4h1M9 21v-3.5a.5.5 0 01.5-.5h5a.5.5 0 01.5.5V21" />
    </svg>,
    // 04 Brand & Growth
    <svg key="04" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>,
    // 05 Investment Readiness
    <svg key="05" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>,
    // 06 Ecosystem Access
    <svg key="06" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>,
  ];

  const pillarMeta = [
    { step: '01', tag: 'Direction', accent: '#fb923c' },
    { step: '02', tag: 'Market Fit', accent: '#f97316' },
    { step: '03', tag: 'Operations', accent: '#fbbf24' },
    { step: '04', tag: 'Visibility', accent: '#06b6d4' },
    { step: '05', tag: 'Fundraising', accent: '#38bdf8' },
    { step: '06', tag: 'Network', accent: '#818cf8' },
  ];

  const total = items.length;

  return (
    <div className="mt-28 relative">
      {/* Background ambient */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] max-w-full h-[500px] bg-gradient-to-r from-[#fb923c]/10 via-[#f97316]/8 to-[#06b6d4]/10 blur-[160px] pointer-events-none -z-10" />

      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10 border-b border-white/10 pb-8">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#fb923c]/10 border border-[#fb923c]/30 text-[#fb923c] text-xs font-semibold tracking-[0.25em] uppercase mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[#fb923c] animate-pulse" />
            <span>VENTURE ACCELERATION · 6 STRATEGIC PILLARS</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight">
            {block.title}
          </h2>
          <p className="mt-2 text-white/55 text-sm sm:text-base leading-relaxed max-w-2xl">
            A structured approach that transforms promising ideas into investment-ready, scalable ventures built for Bharat.
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-white/[0.03] border border-white/10 text-white/60 text-xs font-mono shrink-0">
          <span className="w-2 h-2 rounded-full bg-[#fb923c] animate-pulse" />
          <span>{total} Integrated Pillars</span>
        </div>
      </div>

      {/* Desktop: Bento Grid Layout */}
      <div className="hidden md:grid grid-cols-3 gap-4 lg:gap-5">
        {items.map((item, idx) => {
          const meta = pillarMeta[idx] || { step: `0${idx + 1}`, tag: 'Pillar', accent: '#fb923c' };
          const icon = PILLAR_ICONS[idx];
          const isActive = activeIdx === idx;
          // First item spans 2 cols (hero pillar), rest are normal
          const isHero = idx === 0;

          return (
            <div
              key={item.title}
              onClick={() => setActiveIdx(idx)}
              className={`group relative rounded-2xl lg:rounded-3xl border overflow-hidden cursor-pointer transition-all duration-300 ${isHero ? 'col-span-2 row-span-2' : ''
                } ${isActive
                  ? 'border-[#fb923c]/70 shadow-[0_0_40px_rgba(251,146,60,0.25)] scale-[1.01]'
                  : 'border-white/10 hover:border-white/25 hover:shadow-[0_8px_30px_rgba(0,0,0,0.4)]'
                }`}
              style={{
                background: isActive
                  ? `linear-gradient(135deg, ${meta.accent}18 0%, #0d0d12 50%, #080810 100%)`
                  : 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(8,8,12,0.95) 100%)',
              }}
            >
              {/* Top gradient line */}
              <div
                className="absolute inset-x-0 top-0 h-[2px] opacity-80"
                style={{
                  background: `linear-gradient(to right, transparent, ${meta.accent}, transparent)`,
                }}
              />

              {/* Ambient corner glow */}
              {isActive && (
                <div
                  className="absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl pointer-events-none"
                  style={{ background: `${meta.accent}20` }}
                />
              )}

              <div className={`relative z-10 flex flex-col h-full ${isHero ? 'p-7 lg:p-8 min-h-[280px]' : 'p-5 lg:p-6 min-h-[160px]'}`}>
                {/* Icon + step badge row */}
                <div className="flex items-start justify-between mb-4">
                  <div
                    className="flex items-center justify-center rounded-xl border transition-colors duration-300"
                    style={{
                      width: isHero ? '52px' : '44px',
                      height: isHero ? '52px' : '44px',
                      backgroundColor: `${meta.accent}15`,
                      borderColor: `${meta.accent}40`,
                      color: meta.accent,
                      boxShadow: isActive ? `0 0 18px ${meta.accent}30` : 'none',
                    }}
                  >
                    {icon}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span
                      className="px-2.5 py-0.5 rounded-full border text-[10px] font-mono font-bold"
                      style={{
                        backgroundColor: `${meta.accent}12`,
                        borderColor: `${meta.accent}40`,
                        color: meta.accent,
                      }}
                    >
                      {meta.step}
                    </span>
                    <span className="px-2 py-0.5 rounded-full bg-white/[0.05] border border-white/10 text-[10px] font-mono text-white/50">
                      {meta.tag}
                    </span>
                  </div>
                </div>

                {/* Title */}
                <h3
                  className={`font-bold text-white tracking-tight leading-snug mb-2.5 transition-colors duration-200 ${isHero ? 'text-xl lg:text-2xl' : 'text-base lg:text-lg'
                    } ${isActive ? '' : 'group-hover:text-white/90'}`}
                >
                  {item.title}
                </h3>

                {/* Description */}
                <p
                  className={`text-white/65 leading-relaxed flex-1 ${isHero ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'
                    }`}
                >
                  {item.text}
                </p>

                {/* Bottom divider on hero */}
                {isHero && (
                  <div className="mt-6 pt-4 border-t border-white/[0.08] flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: meta.accent }}
                    />
                    <span className="text-xs text-white/40 font-mono">
                      Foundation Pillar · Bharat Ventures
                    </span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile: Vertical List with active highlight */}
      <div className="md:hidden space-y-3">
        {items.map((item, idx) => {
          const meta = pillarMeta[idx] || { step: `0${idx + 1}`, tag: 'Pillar', accent: '#fb923c' };
          const icon = PILLAR_ICONS[idx];
          const isActive = activeIdx === idx;

          return (
            <div
              key={item.title}
              onClick={() => setActiveIdx(idx)}
              className={`relative rounded-2xl border overflow-hidden cursor-pointer transition-all duration-300 ${isActive
                ? 'border-[#fb923c]/60 shadow-[0_0_30px_rgba(251,146,60,0.2)]'
                : 'border-white/10'
                }`}
              style={{
                background: isActive
                  ? `linear-gradient(135deg, ${meta.accent}15 0%, #0d0d12 100%)`
                  : 'rgba(255,255,255,0.025)',
              }}
            >
              {/* Top line */}
              <div
                className="absolute inset-x-0 top-0 h-[2px]"
                style={{
                  background: `linear-gradient(to right, transparent, ${meta.accent}${isActive ? 'cc' : '50'}, transparent)`,
                }}
              />

              <div className="p-4 flex gap-4">
                {/* Icon */}
                <div
                  className="shrink-0 w-11 h-11 rounded-xl border flex items-center justify-center mt-0.5"
                  style={{
                    backgroundColor: `${meta.accent}15`,
                    borderColor: `${meta.accent}40`,
                    color: meta.accent,
                  }}
                >
                  {icon}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span
                      className="text-[10px] font-mono font-bold"
                      style={{ color: meta.accent }}
                    >
                      {meta.step}
                    </span>
                    <span className="text-[10px] text-white/40 font-mono">· {meta.tag}</span>
                  </div>
                  <h3 className="text-sm font-bold text-white mb-1 leading-snug">{item.title}</h3>
                  <p
                    className={`text-xs text-white/65 leading-relaxed transition-all duration-300 overflow-hidden ${isActive ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'
                      }`}
                  >
                    {item.text}
                  </p>
                  {!isActive && (
                    <p className="text-xs text-white/40 mt-0.5">Tap to expand</p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CircularEcosystemBlock({ block, company, companyName }) {
  const items = block.items || [];
  const total = items.length;
  const [activeIdx, setActiveIdx] = useState(0);

  if (!total) return null;

  const radius = 370;
  const meta = getEcosystemCenterMeta(company, block);

  return (
    <div className="mt-28 relative overflow-hidden">
      <style>{`
        @keyframes orbit-spin-cw {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes orbit-counter-ccw {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(-360deg); }
        }
        .orbit-spin-track {
          animation: orbit-spin-cw 55s linear infinite;
        }
        .orbit-counter-card {
          animation: orbit-counter-ccw 55s linear infinite;
        }
        .orbit-hover-pause:hover .orbit-spin-track,
        .orbit-hover-pause:hover .orbit-counter-card {
          animation-play-state: paused !important;
        }
      `}</style>

      {/* Ambient background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[850px] max-w-full h-[850px] bg-[#d42b92]/10 blur-[180px] pointer-events-none -z-10" />

      {/* Section Header */}
      <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-10 border-b border-white/5 pb-6">
        <div>
          {/* <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[#ff8ec5] text-xs font-semibold tracking-[0.25em] uppercase mb-3 backdrop-blur-md">
            <span className="w-1.5 h-1.5 rounded-full bg-[#d42b92] animate-pulse" />
            <span>CIRCULAR ECOSYSTEM ARCHITECTURE · {total} NODES</span>
          </div> */}
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight">
            {block.title}
          </h2>
          <p className="mt-2 text-white/50 text-sm sm:text-base leading-relaxed max-w-xl">
            A cohesive circular model where learning, practice, and opportunity orbit together.
          </p>
        </div>

        {/* Hover hint */}
        {/* <div className="hidden sm:inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/[0.03] border border-white/10 text-white/60 text-xs font-mono">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <span>Hover card to pause orbit</span>
        </div> */}
      </div>

      {/* Desktop & Tablet: Circular Orbit View (screens >= 768px) */}
      <div className="hidden md:flex relative w-full items-center justify-center py-6 sm:py-10 overflow-hidden">
        {/* Circular Orbit Area with Responsive Scaling */}
        <div className="orbit-hover-pause relative w-[920px] h-[920px] shrink-0 scale-[0.76] lg:scale-[0.95] xl:scale-100 origin-center transition-transform select-none">

          {/* Single Clean Orbital Track Line */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div
              className="rounded-full border border-white/[0.07]"
              style={{ width: `${radius * 2}px`, height: `${radius * 2}px` }}
            />
          </div>

          {/* Rotating Orbit Parent Container */}
          <div className="orbit-spin-track absolute inset-0">
            {items.map((item, idx) => {
              const angleDeg = (idx * 360) / total;
              const IconComponent = ECOSYSTEM_ICONS[idx % ECOSYSTEM_ICONS.length] || Layers;
              const displayIdx = idx < 9 ? `0${idx + 1}` : `${idx + 1}`;
              const isActive = activeIdx === idx;

              return (
                <div
                  key={item.title}
                  className="absolute top-1/2 left-1/2 w-0 h-0"
                  style={{
                    transform: `rotate(${angleDeg}deg) translate(${radius}px) rotate(-${angleDeg}deg)`,
                  }}
                >
                  {/* The Card Counter-Rotates to stay upright and horizontal at all angles! */}
                  <div
                    onClick={() => setActiveIdx(idx)}
                    className={`orbit-counter-card absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[270px] rounded-2xl border p-4 sm:p-5 backdrop-blur-2xl transition-all duration-300 cursor-pointer shadow-2xl ${isActive
                      ? 'border-[#ff8ec5] bg-[#141524] ring-2 ring-[#d42b92] shadow-[0_0_35px_rgba(212,43,146,0.4)] scale-105 z-30'
                      : 'border-white/[0.12] bg-[#0c0d16]/95 hover:border-[#ff8ec5]/60 hover:bg-[#121320] hover:scale-105 z-10'
                      }`}
                  >
                    {/* Top Accent line */}
                    <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#d42b92]/70 to-transparent" />

                    {/* Header inside card */}
                    <div className="flex items-center justify-between mb-2.5">
                      <div className="w-8 h-8 rounded-lg bg-[#d42b92]/20 border border-[#d42b92]/40 text-[#ff8ec5] flex items-center justify-center">
                        <IconComponent className="w-4 h-4" />
                      </div>
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-white/[0.06] border border-white/10 text-white/70">
                        Node {displayIdx}
                      </span>
                    </div>

                    {/* Title */}
                    <h4 className="text-sm font-bold text-white tracking-tight leading-snug mb-1.5">
                      {item.title}
                    </h4>

                    {/* Exact Paragraph Content */}
                    <p className="text-[11px] sm:text-xs text-white/70 leading-relaxed">
                      {item.text}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Highlighted Centered Hub (Fixed In Center, Does NOT rotate) */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
            {/* Luminous Center Orb with Dynamic Venture Ecosystem */}
            <div
              className="relative w-[260px] h-[260px] rounded-full bg-gradient-to-br from-[#1a0a20] via-[#100816] to-[#0a0b12] border-2 backdrop-blur-3xl flex flex-col items-center justify-center p-6 text-center pointer-events-auto"
              style={{
                borderColor: meta.accent,
                boxShadow: `0 0 65px ${meta.glow}, inset 0 0 35px ${meta.accent}30`,
              }}
            >
              {/* Radiant Inner Glow */}
              <div
                className="absolute inset-0 rounded-full blur-md pointer-events-none"
                style={{
                  background: `radial-gradient(circle, ${meta.accent}25 0%, transparent 70%)`,
                }}
              />

              {/* Eyebrow badge */}
              <div
                className="relative z-10 inline-flex items-center px-3 py-1 rounded-full border text-[10px] font-bold tracking-[0.2em] uppercase mb-2"
                style={{
                  backgroundColor: `${meta.accent}18`,
                  borderColor: `${meta.accent}50`,
                  color: meta.accent,
                  boxShadow: `0 0 15px ${meta.accent}30`,
                }}
              >
                <span>CORE HUB</span>
              </div>

              {/* Main Brand Title */}
              {renderCenterBrand(meta, company)}

              <span
                className="relative z-10 text-xs font-bold tracking-[0.22em] uppercase mt-1 drop-shadow-sm"
                style={{ color: meta.accent }}
              >
                {meta.subtitle}
              </span>

              {/* Highlight divider */}
              <div
                className="relative z-10 w-16 h-[2px] my-2.5"
                style={{
                  background: `linear-gradient(to right, transparent, ${meta.accent}, transparent)`,
                }}
              />

              {/* Active node indicator */}
              <div className="relative z-10 text-[11px] text-white/80 max-w-[210px] truncate px-3 py-1 rounded-full bg-white/[0.06] border border-white/10 backdrop-blur-md">
                <span style={{ color: meta.accent }} className="font-semibold">Active:</span> {items[activeIdx]?.title}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Mobile: Purpose-Built Responsive Ecosystem (screens < 768px) */}
      <div className="block md:hidden space-y-6">
        {/* Highlighted Venture Ecosystem Mobile Hub */}
        <div
          className="relative w-full max-w-[320px] mx-auto rounded-3xl bg-gradient-to-br from-[#1a0a20] via-[#100816] to-[#0a0b12] border-2 backdrop-blur-3xl p-6 text-center overflow-hidden"
          style={{
            borderColor: meta.accent,
            boxShadow: `0 0 45px ${meta.glow}`,
          }}
        >
          <div
            className="absolute inset-0 blur-xl pointer-events-none"
            style={{
              background: `radial-gradient(circle, ${meta.accent}20 0%, transparent 70%)`,
            }}
          />

          {/* Eyebrow badge */}
          <div
            className="relative z-10 inline-flex items-center px-3 py-1 rounded-full border text-[10px] font-bold tracking-[0.2em] uppercase mb-2"
            style={{
              backgroundColor: `${meta.accent}18`,
              borderColor: `${meta.accent}50`,
              color: meta.accent,
            }}
          >
            <span>CORE HUB</span>
          </div>

          {/* Main Title */}
          {renderCenterBrand(meta, company)}

          <p
            className="relative z-10 text-xs font-bold tracking-[0.2em] uppercase mt-0.5"
            style={{ color: meta.accent }}
          >
            {meta.subtitle}
          </p>

          <div
            className="relative z-10 w-12 h-[2px] mx-auto my-2.5"
            style={{
              background: `linear-gradient(to right, transparent, ${meta.accent}, transparent)`,
            }}
          />

          <p className="relative z-10 text-[11px] text-white/60">
            Node {activeIdx + 1} of {total} ·{' '}
            <span style={{ color: meta.accent }} className="font-semibold">
              {items[activeIdx]?.title}
            </span>
          </p>
        </div>

        {/* Interactive Responsive Node Selector Pills (Wrapped to fit all mobile screens) */}
        <div className="flex flex-wrap items-center justify-center gap-1.5 w-full max-w-full py-1">
          {items.map((item, idx) => {
            const isCurrent = activeIdx === idx;
            const displayIdx = idx < 9 ? `0${idx + 1}` : `${idx + 1}`;
            return (
              <button
                key={item.title}
                type="button"
                onClick={() => setActiveIdx(idx)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-mono transition-all cursor-pointer ${isCurrent
                  ? 'bg-[#d42b92] text-white border border-[#ff8ec5] shadow-[0_0_15px_rgba(212,43,146,0.5)] font-semibold scale-105'
                  : 'bg-white/[0.04] text-white/70 border border-white/10 hover:border-white/20'
                  }`}
              >
                <span className="text-[10px] opacity-75">{displayIdx}</span>
                <span className="font-sans text-[11px] font-medium">{item.title}</span>
              </button>
            );
          })}
        </div>

        {/* Active Node Featured Card on Mobile */}
        {(() => {
          const item = items[activeIdx];
          const IconComponent = ECOSYSTEM_ICONS[activeIdx % ECOSYSTEM_ICONS.length] || Layers;
          const displayIdx = activeIdx < 9 ? `0${activeIdx + 1}` : `${activeIdx + 1}`;

          return (
            <div className="relative rounded-3xl border border-[#ff8ec5]/60 bg-gradient-to-b from-[#141524] to-[#0c0d16] p-6 shadow-2xl overflow-hidden">
              {/* Top Accent line */}
              <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#d42b92] to-transparent" />

              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#d42b92]/20 border border-[#d42b92]/40 text-[#ff8ec5] flex items-center justify-center">
                  <IconComponent className="w-5 h-5" />
                </div>
                <span className="text-xs font-mono px-3 py-1 rounded-full bg-white/[0.06] border border-white/10 text-white/80 font-semibold">
                  Node {displayIdx} of 0{total}
                </span>
              </div>

              <h4 className="text-lg font-bold text-white tracking-tight mb-2">
                {item?.title}
              </h4>
              <p className="text-sm text-white/80 leading-relaxed">
                {item?.text}
              </p>

              {/* Prev / Next mobile touch controls */}
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setActiveIdx((prev) => (prev - 1 + total) % total)}
                  className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/[0.05] hover:bg-white/[0.1] border border-white/10 text-white/80 text-xs font-medium transition-colors cursor-pointer"
                >
                  <ChevronLeft className="w-4 h-4" /> Previous
                </button>
                <div className="flex items-center gap-1.5">
                  {items.map((_, i) => (
                    <span
                      key={i}
                      onClick={() => setActiveIdx(i)}
                      className={`h-1.5 rounded-full transition-all cursor-pointer ${activeIdx === i ? 'w-5 bg-[#ff8ec5]' : 'w-1.5 bg-white/20'
                        }`}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setActiveIdx((prev) => (prev + 1) % total)}
                  className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#d42b92] hover:bg-[#e0339f] text-white text-xs font-semibold shadow-md shadow-[#d42b92]/30 transition-colors cursor-pointer"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

const AUDIENCE_META = {
  // enCODE
  students: {
    icon: GraduationCap,
    color: '#ffd043',
    keyword: 'Students',
  },
  learners: {
    icon: BookOpen,
    color: '#d42b92',
    keyword: 'Learners',
  },
  'emerging designers': {
    icon: PenTool,
    color: '#d42b92',
    keyword: 'Emerging designers',
  },
  professionals: {
    icon: Briefcase,
    color: '#ffd043',
    keyword: 'Professionals',
  },
  mentors: {
    icon: Users,
    color: '#ffd043',
    keyword: 'Mentors',
  },
  institutions: {
    icon: Landmark,
    color: '#d42b92',
    keyword: 'Institutions',
  },
  organisations: {
    icon: Building2,
    color: '#ffd043',
    keyword: 'Organisations',
  },
  organizations: {
    icon: Building2,
    color: '#ffd043',
    keyword: 'Organizations',
  },

  // Beyond Ability X (BAX)
  'athletes with disabilities': {
    icon: Trophy,
    color: '#a855f7',
    keyword: 'Athletes with disabilities',
  },
  'emerging players': {
    icon: GraduationCap,
    color: '#ff3b6d',
    keyword: 'Emerging players',
  },
  'coaches, academies': {
    icon: Target,
    color: '#ff3b6d',
    keyword: 'Coaches, academies and sporting bodies',
  },
  'families and communities': {
    icon: Users,
    color: '#a855f7',
    keyword: 'Families and communities',
  },
  'brands and organisations': {
    icon: Building2,
    color: '#a855f7',
    keyword: 'Brands and organisations',
  },
  'sports bodies': {
    icon: Landmark,
    color: '#ff3b6d',
    keyword: 'Sports bodies and institutions',
  },

  // Bharat Ventures
  'early-stage startups': {
    icon: Lightbulb,
    color: '#f97316',
    keyword: 'Early-stage startups',
  },
  'founders building': {
    icon: Compass,
    color: '#06b6d4',
    keyword: 'Founders',
  },
  'consumer and d2c': {
    icon: TrendingUp,
    color: '#f97316',
    keyword: 'Consumer and D2C brands',
  },
  'technology-led ventures': {
    icon: Cpu,
    color: '#06b6d4',
    keyword: 'Technology-led ventures',
  },
  'impact-driven enterprises': {
    icon: Leaf,
    color: '#f97316',
    keyword: 'Impact-driven enterprises',
  },
  'entrepreneurs seeking': {
    icon: Briefcase,
    color: '#06b6d4',
    keyword: 'Entrepreneurs',
  },

  // CTPL
  'universities and': {
    icon: Landmark,
    color: '#3b82f6',
    keyword: 'Universities and higher-education institutions',
  },
  'colleges and': {
    icon: BookOpen,
    color: '#10b981',
    keyword: 'Colleges and professional education providers',
  },
  'schools and': {
    icon: GraduationCap,
    color: '#3b82f6',
    keyword: 'Schools and academic groups',
  },
  'institutional admissions': {
    icon: Users,
    color: '#10b981',
    keyword: 'Institutional admissions and outreach teams',
  },
  'education leaders': {
    icon: Lightbulb,
    color: '#3b82f6',
    keyword: 'Education leaders',
  },
  'organisations developing': {
    icon: Building2,
    color: '#10b981',
    keyword: 'Organisations developing industry-aligned learning programmes',
  },
};

const renderAudienceTitle = (title, company) => {
  const shortNames = {
    'beyond-ability-x': { name: 'BAX', color: '#a855f7' },
    encode: { name: 'enCODE', color: '#ffd043' },
    'bharat-ventures': { name: 'Bharat Ventures', color: '#f97316' },
    ctpl: { name: 'CTPL', color: '#3b82f6' },
    i2i: { name: 'i2i', color: '#e11d48' },
    innovz: { name: 'InnovZ', color: '#06b6d4' },
    innovidea: { name: 'InnovIdea', color: '#d42b92' },
  };

  const config = shortNames[company.slug] || { name: company.name, color: '#ffd043' };
  const target = title.includes(config.name)
    ? config.name
    : title.includes(company.name)
      ? company.name
      : null;

  if (!target) return title;

  const parts = title.split(target);
  return (
    <>
      {parts[0]}
      <span style={{ color: config.color }}>{target}</span>
      {parts[1]}
    </>
  );
};

function CompanyHeroIntro({ company, heroShot }) {
  return (
    <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
      <div className="lg:col-span-7 flex flex-col justify-center">
        {company.logo && (
          <div className="inline-flex items-center justify-center rounded-2xl bg-white px-5 py-3 shadow-md mb-8 max-w-fit">
            <img
              src={company.logo}
              alt={`${company.name} logo`}
              className="h-9 sm:h-11 w-auto max-w-full object-contain"
            />
          </div>
        )}

        <div className="space-y-6">
          {company.intro?.map((p, idx) => {
            if (idx === 0) {
              return (
                <h2
                  key={idx}
                  className="text-2xl sm:text-3xl lg:text-[34px] font-bold text-white leading-snug tracking-tight"
                >
                  {p}
                </h2>
              );
            }

            if (p.includes('explore their interests') && p.includes('build relevant skills')) {
              return (
                <p
                  key={idx}
                  className="text-base sm:text-lg text-white/70 leading-relaxed font-normal"
                >
                  Built for learners navigating creative and interdisciplinary careers, enCODE goes beyond
                  passive course consumption. It enables learners to{' '}
                  <strong className="text-white font-semibold">explore their interests</strong>,{' '}
                  <strong className="text-white font-semibold underline decoration-[#ffd043] decoration-2 underline-offset-4">
                    build relevant skills
                  </strong>
                  ,{' '}
                  <strong className="text-white font-semibold">apply their knowledge</strong> and{' '}
                  <strong className="text-white font-semibold">create visible evidence</strong> of
                  their capabilities through projects, challenges and portfolio-based experiences.
                </p>
              );
            }

            return (
              <p
                key={idx}
                className="text-base sm:text-lg text-white/70 leading-relaxed font-normal"
              >
                {p}
              </p>
            );
          })}
        </div>
      </div>

      <div className="lg:col-span-5">
        {heroShot ? (
          <div className="rounded-[28px] sm:rounded-[36px] overflow-hidden border border-white/10 shadow-2xl relative aspect-[4/3] w-full max-w-full group">
            <img
              src={heroShot}
              alt={company.name}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function CompanyStatementSection({ statement, company }) {
  if (!statement) return null;

  const isBAX =
    company?.slug === 'beyond-ability-x' ||
    statement.title?.toLowerCase().includes('built around ability');
  const isEnCodeSteps =
    company?.slug === 'encode' || statement.title === 'Learn. Create. Connect. Collaborate.';

  // If enCODE with the 4 steps:
  if (isEnCodeSteps) {
    const rawSteps = statement.title
      .split('.')
      .map((s) => s.trim())
      .filter(Boolean);
    const stepIcons = [GraduationCap, PenTool, Users, Share2];

    return (
      <div className="mt-12 sm:mt-16 lg:mt-20 rounded-[28px] sm:rounded-[32px] border border-[#d42b92]/30 bg-gradient-to-br from-[#d42b92]/15 via-[#130716]/90 to-[#0a050d] p-6 sm:p-10 lg:p-12 relative overflow-hidden backdrop-blur-md shadow-[0_10px_35px_rgba(0,0,0,0.5)]">
        <div className="grid grid-cols-2 md:flex md:flex-wrap lg:flex-nowrap items-center justify-between gap-4 sm:gap-6">
          {rawSteps.map((step, idx) => {
            const IconComp = stepIcons[idx % stepIcons.length] || Lightbulb;
            return (
              <React.Fragment key={step}>
                <div className="flex items-center gap-3 sm:gap-4 shrink-0">
                  <div className="w-11 h-11 sm:w-13 sm:h-13 rounded-full border border-[#d42b92]/70 bg-[#d42b92]/15 flex items-center justify-center text-[#ff4bb0] shadow-[0_0_15px_rgba(212,43,146,0.25)] shrink-0">
                    <IconComp className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <span className="text-lg sm:text-2xl font-bold text-white tracking-tight">
                    {step}.
                  </span>
                </div>
                {idx < rawSteps.length - 1 && (
                  <div className="hidden lg:block h-[1.5px] flex-1 bg-gradient-to-r from-[#d42b92]/60 to-[#d42b92]/20 mx-2 xl:mx-4" />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <div className="w-full h-[1px] bg-white/10 my-6 sm:my-8" />
        <p className="text-white/80 text-sm sm:text-base lg:text-[17px] leading-relaxed max-w-5xl font-normal">
          {statement.text}
        </p>
      </div>
    );
  }

  // If Beyond Ability X (BAX):
  if (isBAX) {
    return (
      <div className="mt-12 sm:mt-16 lg:mt-20 rounded-[28px] sm:rounded-[32px] border border-[#ff3b6d]/30 bg-gradient-to-br from-[#8b5cf6]/15 via-[#130716]/90 to-[#0a050d] p-6 sm:p-10 lg:p-12 relative overflow-hidden backdrop-blur-md shadow-[0_10px_35px_rgba(0,0,0,0.5)]">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4 sm:mb-6">
          Beyond disability. Built around <span className="text-[#ff3b6d]">ability.</span>
        </h2>
        <p className="text-white/80 text-sm sm:text-base lg:text-[17px] leading-relaxed max-w-5xl font-normal">
          {statement.text}
        </p>
      </div>
    );
  }

  // General statement card:
  return (
    <div className="mt-12 sm:mt-16 lg:mt-20 rounded-[28px] sm:rounded-[32px] border border-white/10 bg-gradient-to-br from-white/[0.05] via-[#130716]/80 to-[#0a050d] p-6 sm:p-10 lg:p-12 relative overflow-hidden backdrop-blur-md shadow-[0_10px_35px_rgba(0,0,0,0.5)]">
      <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4 sm:mb-6">
        {statement.title}
      </h2>
      <p className="text-white/80 text-sm sm:text-base lg:text-[17px] leading-relaxed max-w-5xl font-normal">
        {statement.text}
      </p>
    </div>
  );
}

function CompanyAudienceSection({ company }) {
  if (!company.audience || company.audience.length === 0) return null;

  const isBAX = company.slug === 'beyond-ability-x';
  const hasNotch = company.slug === 'encode';
  const title = company.audienceTitle || `Who ${company.name} is for`;
  const total = company.audience.length;

  return (
    <div className="mt-16 sm:mt-24">
      {/* Section Header */}
      <div className="grid md:grid-cols-12 gap-6 lg:gap-12 items-end mb-8 sm:mb-12">
        <div className="md:col-span-7 lg:col-span-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
            {renderAudienceTitle(title, company)}
          </h2>
        </div>
        <div className="md:col-span-5 lg:col-span-4">
          <p className="text-white/60 text-sm sm:text-base leading-relaxed">
            Built for people and organisations who want to participate with clarity and intent.
          </p>
        </div>
      </div>

      {/* Responsive Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
        {company.audience.map((item, index) => {
          const lower = item.toLowerCase();
          let matched = null;
          for (const [key, val] of Object.entries(AUDIENCE_META)) {
            if (lower.startsWith(key)) {
              matched = val;
              break;
            }
          }

          if (!matched) {
            const isEven = index % 2 === 0;
            const words = item.split(' ');
            matched = {
              icon: isEven ? Briefcase : Users,
              color: isEven ? '#a855f7' : '#ff3b6d',
              keyword: words[0] || 'Target',
            };
          }

          let keyword = matched.keyword;
          let rest = item;
          if (item.toLowerCase().startsWith(keyword.toLowerCase())) {
            rest = item.slice(keyword.length);
          } else {
            const verbs = [
              'seeking',
              'requiring',
              'advancing',
              'supporting',
              'committed',
              'working',
              'exploring',
              'building',
              'looking',
            ];
            let splitIdx = -1;
            for (const v of verbs) {
              const idx = item.toLowerCase().indexOf(' ' + v + ' ');
              if (idx !== -1) {
                splitIdx = idx;
                break;
              }
            }
            if (splitIdx !== -1) {
              keyword = item.slice(0, splitIdx);
              rest = item.slice(splitIdx);
            }
          }

          const isFullWidth = hasNotch && index === total - 1 && total % 2 !== 0;
          const IconComp = matched.icon;
          const accentColor = matched.color;

          return (
            <div
              key={item}
              className={`group rounded-2xl sm:rounded-3xl p-5 sm:p-6 transition-all duration-300 relative overflow-hidden bg-[#101015]/95 hover:bg-[#15151c] ${isFullWidth
                ? 'md:col-span-2 border border-[#d42b92]/40 shadow-[0_0_25px_rgba(212,43,146,0.12)] hover:border-[#d42b92]/70'
                : isBAX
                  ? 'border hover:scale-[1.01]'
                  : 'border border-white/[0.08] hover:border-white/20'
                }`}
              style={
                isBAX
                  ? {
                    borderColor: `${accentColor}55`,
                    boxShadow: `0 0 20px ${accentColor}12`,
                  }
                  : undefined
              }
            >
              {/* Top-left accent notch bar (for encode or cards with notch) */}
              {hasNotch && (
                <div
                  className="w-7 sm:w-8 h-1 rounded-full mb-3.5"
                  style={{ backgroundColor: accentColor }}
                />
              )}

              {/* Card Body */}
              <div className="flex items-center gap-4 sm:gap-5">
                {/* Icon Badge */}
                <div
                  className="w-13 h-13 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl border flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-105"
                  style={{
                    borderColor: `${accentColor}40`,
                    backgroundColor: `${accentColor}12`,
                    color: accentColor,
                  }}
                >
                  <IconComp className="w-6 h-6 sm:w-7 sm:h-7" />
                </div>

                {/* Text with highlighted keyword */}
                <p className="text-sm sm:text-base lg:text-[17px] text-white/80 leading-snug">
                  <strong className="font-bold" style={{ color: accentColor }}>
                    {keyword}
                  </strong>
                  {rest}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CompanyPage() {
  const { slug } = useParams();
  const company = getCompany(slug);

  if (!company) return <Navigate to="/" replace />;

  const path = `/ventures/${company.slug}`;
  const heroShot = company.gallery?.[0];
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: company.seoTitle,
    description: company.seoDescription,
    url: `${SITE}${path}`,
    isPartOf: { '@type': 'WebSite', name: 'Innovher', url: SITE },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
        { '@type': 'ListItem', position: 2, name: 'Ventures', item: `${SITE}/#ventures` },
        { '@type': 'ListItem', position: 3, name: company.name, item: `${SITE}${path}` },
      ],
    },
  };

  return (
    <>
      <SEO
        title={company.seoTitle}
        description={company.seoDescription}
        keywords={company.keywords}
        path={path}
        jsonLd={jsonLd}
      />
      <PageHeader
        eyebrow={company.category}
        title={company.name}
        subtitle={company.headline}
        breadcrumbs={[{ label: 'Ventures', to: '/#ventures' }, { label: company.name }]}
      />

      <section className="relative bg-[#08080a] py-14 sm:py-20 lg:py-28 overflow-hidden w-full max-w-full">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <CompanyHeroIntro company={company} heroShot={heroShot} />

          <CompanyStatementSection statement={company.statement} company={company} />

          <CompanyAudienceSection company={company} />

          <VentureGallery company={company} />

          {company.blocks?.map((block) => {
            const isCtplAdmissions =
              company.slug === 'ctpl' &&
              (block.title.toLowerCase().includes('admissions transformation') ||
                block.title.toLowerCase().includes('end-to-end'));

            if (isCtplAdmissions) {
              return (
                <CtplAdmissionsPipelineBlock
                  key={block.title}
                  block={block}
                  company={company}
                />
              );
            }

            // Bharat Ventures gets a unique bento-grid pillar layout
            const isBharatVenturesPillar =
              company.slug === 'bharat-ventures' &&
              block.title.toLowerCase().includes('approach');

            if (isBharatVenturesPillar) {
              return (
                <BharatVenturesPillarBlock
                  key={block.title}
                  block={block}
                />
              );
            }

            const isCircular =
              block.title.toLowerCase().includes('ecosystem') ||
              block.items.length >= 6;

            if (isCircular) {
              return (
                <CircularEcosystemBlock
                  key={block.title}
                  block={block}
                  company={company}
                  companyName={company.name}
                />
              );
            }

            return (
              <div key={block.title} className="mt-20">
                <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-end mb-8">
                  <h2 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">{block.title}</h2>
                  <div className="hidden lg:block h-px bg-white/10 mb-2" aria-hidden />
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {block.items.map((item) => (
                    <article key={item.title} className="rounded-3xl border border-white/10 bg-white/[0.02] p-6 hover:border-[#d42b92]/50 transition-colors">
                      <h3 className="text-lg font-semibold text-white">{item.title}</h3>
                      <p className="mt-3 text-sm text-white/65 leading-relaxed">{item.text}</p>
                    </article>
                  ))}
                </div>
              </div>
            );
          })}

          {company.prose?.length > 0 && (
            <CompanyProseSection prose={company.prose} />
          )}

          {company.lists?.map((list, listIdx) => (
            <CompanyListSection key={list.title} list={list} index={listIdx} />
          ))}

          {company.roles && (
            <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {company.roles.map((role) => (
                <article key={role.title} className="rounded-3xl border border-white/10 p-6 bg-white/[0.02]">
                  <h3 className="text-white font-semibold">{role.title}</h3>
                  <p className="mt-2 text-sm text-white/65 leading-relaxed">{role.text}</p>
                </article>
              ))}
            </div>
          )}

          <div className="mt-20 rounded-[2rem] border border-white/10 bg-black p-8 lg:p-12 grid lg:grid-cols-2 gap-8 lg:gap-12 items-end">
            <div>
              <h2 className="text-3xl font-semibold text-white tracking-tight">{company.close.title}</h2>
              <p className="mt-4 text-white/70 text-lg leading-relaxed">{company.close.text}</p>
              <p className="mt-6 text-[#ff8ec5] font-medium">{company.close.line}</p>
            </div>
            <div className="lg:justify-self-end">
              {company.cta?.external ? (
                <a
                  href={company.cta.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#d42b92] text-white font-semibold hover:bg-white hover:text-black transition-colors"
                >
                  {company.cta.label}
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              ) : (
                <Link
                  to={company.cta.to}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#d42b92] text-white font-semibold hover:bg-white hover:text-black transition-colors"
                >
                  {company.cta.label}
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
